import * as React from "react";
import { Home, Mail, MapPin } from "lucide-react";

import { cn } from "@/shared/lib/cn";

export interface AddressInfoProps extends React.HTMLAttributes<HTMLDivElement> {
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | number | null;
  email1?: string | null;
  email2?: string | null;
}

/**
 * Stacked address / email renderer, rebuilt from the old
 * `@/components/common/AddressInfo` onto shared/ui tokens. Each present line is
 * shown with a leading (decorative) icon; absent lines are omitted. The
 * city/state/zip parts are joined with commas, skipping blanks.
 *
 * A generic display component that forwards its ref and extra props to the
 * root element so callers can style or measure it.
 */
const AddressInfo = React.forwardRef<HTMLDivElement, AddressInfoProps>(function AddressInfo(
  { address1, city, state, zip, email1, email2, className, ...props },
  ref
) {
  const cityLine = [city, state, zip].filter(Boolean).join(", ");

  return (
    <div ref={ref} className={cn("flex flex-col text-sm", className)} {...props}>
      {address1 ? (
        <span className="flex items-center gap-1">
          <Home className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          {address1}
        </span>
      ) : null}
      {cityLine ? (
        <span className="flex items-center gap-1">
          <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          {cityLine}
        </span>
      ) : null}
      {email1 ? (
        <span className="flex items-center gap-1">
          <Mail className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          {email1}
        </span>
      ) : null}
      {email2 ? (
        <span className="flex items-center gap-1">
          <Mail className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
          {email2}
        </span>
      ) : null}
    </div>
  );
});

export { AddressInfo };
